import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {
    OrganisationDetail,
    OrganisationLinkedContract,
    OrganisationLinkedService
} from '@shared/api/models/query/organisation.model';
import {DeleteOrganisationModal} from './modals/delete-organisation.modal';

/**
 * UNIVAPLAT-3693: Delete action for the General details tab of the organisation detail page.
 * The parent only renders it for DELETE_ORGANISATION and an active or deactivated organisation; the
 * API enforces both. While linked contracts or services exist the button is disabled and a warning
 * lists them. The backend owns the error texts; no organisation data is logged.
 */
@Component({
    selector: 'cdss-organisation-delete-panel',
    standalone: true,
    templateUrl: './organisation-delete-panel.component.html',
    styleUrls: ['./organisation-delete-panel.component.scss']
})
export class OrganisationDeletePanelComponent {
    @Input() organisation!: OrganisationDetail;
    @Input() linkedContracts: OrganisationLinkedContract[] = [];
    @Input() linkedServices: OrganisationLinkedService[] = [];
    /** Emitted once the organisation is deleted; the page navigates back to the list. */
    @Output() deleted = new EventEmitter<void>();

    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) {
    }

    get isLinked(): boolean {
        return this.linkedContracts.length > 0 || this.linkedServices.length > 0;
    }

    async openDelete(): Promise<void> {
        if (!this.organisation || this.isLinked) {
            return;
        }
        const ref = this.modalService.open(DeleteOrganisationModal, {
            ariaLabelledBy: 'delete-organisation-title',
            centered: true,
        });
        ref.componentInstance.organisationId = this.organisation.id;
        let confirmed = false;
        try {
            confirmed = (await ref.result) === true;
        } catch {
            // Dismissed (Cancel, close icon, Escape): nothing to do.
        }
        if (confirmed) {
            this.toastr.success('Organisation deleted', 'Success');
            this.deleted.emit();
        }
    }
}
